import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFormValidationTranslations } from '@common/i18n/formValidation';
import type { JSONCompatible } from '@common/types';
import type { QuestionSchema, QuestionValidationSchema } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { parseMultiSelectValue } from '@common/utils/intakeFieldFormatting';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    type SelectChangeEvent,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import CurrencyInput from '@/components/CurrencyInput';
import GooglePlacesAutocomplete from '@/components/GooglePlacesAutocomplete';
import PercentageInput from '@/components/PercentageInput';
import type { ContactBlockEntryErrors } from '@/pages/QuoteIntakeClientView/IntakeFormView/contactValidation';
import ContactBlockQuestion from './ContactBlockQuestion';
import {
    radioErrorStyles,
    radioStyles,
    selectFieldStyles,
    textAreaStyles,
    textFieldStyle,
} from './formFieldStyles';
import FormUploadField from './FormUploadField';
import TableFormComponent from './TableFormComponent';
import { sortMultiSelectByOptions, stringifyMultiSelectValue } from './tableValues';

export interface FormFieldInputProps {
    schema: QuestionSchema;
    validation?: QuestionValidationSchema | null;
    value?: JSONCompatible;
    isError?: boolean;
    helper?: string;
    tabindex?: string | number;
    disabled?: boolean;
    readOnly?: boolean;
    showSelectBoxValue?: boolean;
    typeOfIdValue?: string;
    lawFirmId?: string | number;
    language?: TemplateFormLanguage;
    entryErrors?: ContactBlockEntryErrors[];
    onFieldValueChange?: (name: string, value: JSONCompatible) => void;
}

export default function FormFieldInput({
    schema,
    validation,
    value,
    isError,
    helper,
    tabindex,
    disabled,
    readOnly,
    showSelectBoxValue,
    typeOfIdValue,
    lawFirmId,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    entryErrors,
    onFieldValueChange,
}: FormFieldInputProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DatePickerInputRef = useRef<HTMLInputElement>(null);
    const formValidationTranslations = getFormValidationTranslations(language);

    const {
        type,
        name,
        options,
        placeholder,
        radioOrientation,
        selectOptions,
        isOtherTextInputEnable,
        typeOfDocument,
        columns,
    } = schema;
    const resolvedValue = value ?? schema.value;
    // For the props typed as a number. React also passes the lowercase tabindex through as is
    const tabIndex = tabindex === undefined ? undefined : Number(tabindex);

    useEffect(() => {
        if (DatePickerInputRef.current) {
            DatePickerInputRef.current.setAttribute('tabindex', `${tabindex}`);
        }
    }, [tabindex]);

    switch (type) {
        case 'radio': {
            const questionToBeRenderedAsSelectType = [
                'purchasersListedOnPurchaseAndSaleAgreement',
                'sellersListedOnPurchaseAndSaleAgreement',
                'borrowersList',
            ];
            if (questionToBeRenderedAsSelectType.includes(name)) {
                const alteredValue = resolvedValue || '';

                const effectiveOptions = Array.from({ length: 10 }, (_, i) => ({
                    optionLabel: `${i + 1}`,
                    optionValue: `${i + 1}`,
                }));

                return (
                    <>
                        <FormControl
                            sx={{
                                '& .MuiFormHelperText-root': { marginLeft: '0px' },
                                marginTop: '4px',
                            }}
                            fullWidth
                        >
                            <Select
                                inputProps={{ tabindex: tabindex }}
                                error={isError}
                                disabled={disabled}
                                name={name}
                                placeholder={placeholder}
                                sx={{
                                    ...selectFieldStyles,
                                    pointerEvents: readOnly ? 'none' : undefined,
                                }}
                                value={alteredValue}
                                onChange={(event: SelectChangeEvent<any>) => {
                                    if (!readOnly && onFieldValueChange) {
                                        onFieldValueChange(name, event.target.value);
                                    }
                                }}
                            >
                                {placeholder && (
                                    <MenuItem disabled key="placeholder" value="">
                                        {placeholder}
                                    </MenuItem>
                                )}
                                {effectiveOptions?.map((option) => (
                                    <MenuItem key={option.optionLabel} value={option.optionValue}>
                                        {option.optionLabel}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                        </FormControl>

                        {showSelectBoxValue && (
                            <Box>
                                {effectiveOptions?.map((option, index) => (
                                    <Typography key={index} variant="body2" sx={selectFieldStyles}>
                                        {option.optionLabel}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </>
                );
            }

            let alteredValue = '';
            let alteredInputValue = resolvedValue || '';
            let renderOtherTextField = false;
            const optionsValueArr = options?.map((opt) => opt.optionValue?.toString());

            if (disabled) {
                alteredValue = '';
                if (isOtherTextInputEnable) {
                    renderOtherTextField = true;
                }
            } else {
                if (isOtherTextInputEnable) {
                    if (optionsValueArr?.includes(resolvedValue) && resolvedValue !== 'Other') {
                        alteredValue = resolvedValue;
                        renderOtherTextField = false;
                    } else if (
                        resolvedValue === 'Other' ||
                        (!optionsValueArr?.includes(resolvedValue) && resolvedValue !== '')
                    ) {
                        alteredValue = 'Other';
                        alteredInputValue = resolvedValue !== 'Other' ? resolvedValue : '';
                        renderOtherTextField = true;
                    }
                } else {
                    alteredValue = resolvedValue;
                    renderOtherTextField = false;
                }
            }

            return (
                <Box sx={{ width: '100%' }}>
                    <RadioGroup
                        row={radioOrientation === 'horizontal'}
                        sx={{
                            marginTop: '4px',
                            marginLeft: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        name={name}
                        value={alteredValue || ''}
                        onClick={(event) => {
                            const target = event.target as HTMLElement;

                            if (!readOnly && onFieldValueChange) {
                                if (
                                    target.tagName === 'INPUT' &&
                                    renderOtherTextField &&
                                    alteredValue === 'Other'
                                ) {
                                    onFieldValueChange(name, '');
                                } else if (target.tagName === 'INPUT' && alteredValue !== '') {
                                    onFieldValueChange(name, '');
                                }
                            }
                        }}
                        onChange={(event) => {
                            if (!readOnly && onFieldValueChange) {
                                const selectedValue = event.target.value;

                                if (selectedValue === 'Other') {
                                    renderOtherTextField = true;
                                    alteredInputValue = '';
                                } else {
                                    renderOtherTextField = false;
                                }

                                onFieldValueChange(name, selectedValue);
                            }
                        }}
                    >
                        {options?.map((option, index) => (
                            <FormControlLabel
                                tabIndex={tabIndex}
                                key={index}
                                value={option.optionValue}
                                control={<Radio />}
                                sx={{
                                    ...(isError ? radioErrorStyles(theme) : radioStyles),
                                }}
                                disabled={disabled}
                                label={option.optionLabel}
                            />
                        ))}
                        {renderOtherTextField && isOtherTextInputEnable && (
                            <TextField
                                value={alteredInputValue}
                                disabled={disabled}
                                error={isError}
                                onClick={(event) => {
                                    event.stopPropagation();
                                }}
                                onChange={(event) => {
                                    if (!readOnly && onFieldValueChange) {
                                        const inputValue = event.target.value;
                                        alteredInputValue = inputValue;
                                        onFieldValueChange(name, inputValue || 'Other');
                                    }
                                }}
                            />
                        )}
                    </RadioGroup>
                    <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                </Box>
            );
        }

        case 'text':
            return (
                <Box sx={{ width: '100%' }}>
                    <TextField
                        inputProps={{ tabindex: tabindex }}
                        sx={{
                            ...textFieldStyle,
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        name={name}
                        placeholder={placeholder}
                        disabled={disabled}
                        value={resolvedValue ?? ''}
                        helperText={helper}
                        error={isError}
                        onChange={
                            onFieldValueChange &&
                            ((event) => !readOnly && onFieldValueChange(name, event.target.value))
                        }
                    />
                </Box>
            );
        case 'percentage':
            return (
                <Box sx={{ width: '100%' }}>
                    <PercentageInput
                        tabindex={tabindex}
                        sx={{
                            width: '100%',
                            '& .MuiFormHelperText-root': {
                                marginLeft: '0px',
                            },
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        value={resolvedValue ?? ''}
                        name={name}
                        allowDecimal={true}
                        placeholder={placeholder}
                        disabled={disabled}
                        error={isError}
                        helperText={helper}
                        onChange={
                            onFieldValueChange &&
                            ((inputValue) => !readOnly && onFieldValueChange(name, inputValue))
                        }
                    />
                </Box>
            );
        case 'address':
            return (
                <Box sx={{ width: '100%' }}>
                    <GooglePlacesAutocomplete
                        disabled={disabled}
                        tabindex={tabindex}
                        name={name}
                        language={language}
                        value={resolvedValue || ''}
                        isError={isError}
                        helperText={helper}
                        onChange={(addressValue) => {
                            if (!readOnly && onFieldValueChange) {
                                onFieldValueChange(name, addressValue);
                            }
                        }}
                    />
                </Box>
            );
        case 'currency':
            return (
                <Box sx={{ width: '100%' }}>
                    <CurrencyInput
                        tabindex={tabindex}
                        sx={{
                            width: '100%',
                            '& .MuiFormHelperText-root': {
                                marginLeft: '0px',
                            },
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        value={resolvedValue}
                        name={name}
                        allowDecimal={true}
                        placeholder={placeholder}
                        disabled={disabled}
                        error={isError}
                        helperText={helper}
                        onChange={
                            onFieldValueChange &&
                            ((inputValue) => !readOnly && onFieldValueChange(name, inputValue))
                        }
                    />
                </Box>
            );
        case 'textarea':
            return (
                <Box sx={{ width: '100%' }}>
                    <TextField
                        inputProps={{ tabindex: tabindex }}
                        sx={{
                            ...textAreaStyles,
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        multiline
                        rows={3}
                        name={name}
                        placeholder={placeholder}
                        disabled={disabled}
                        error={isError}
                        helperText={helper}
                        value={resolvedValue}
                        onChange={
                            onFieldValueChange &&
                            ((event) => !readOnly && onFieldValueChange(name, event.target.value))
                        }
                    />
                </Box>
            );
        case 'upload': {
            let fileIds: string[] | undefined;
            if (typeof resolvedValue === 'string' && resolvedValue) {
                fileIds = resolvedValue
                    .split(',')
                    .map((id) => id.trim())
                    .filter(Boolean);
            } else if (Array.isArray(resolvedValue)) {
                fileIds = resolvedValue
                    .map((file) => (typeof file === 'string' ? file : file?.id))
                    .filter(Boolean);
            }

            return (
                <>
                    <FormUploadField
                        tabindex={tabindex}
                        name={name}
                        language={language}
                        fileIds={fileIds}
                        selectedLawFirmId={lawFirmId}
                        isError={isError}
                        disabled={disabled || readOnly}
                        typeOfIdValue={typeOfIdValue}
                        typeofDocument={typeOfDocument}
                        onFileIdsChange={(ids: string[]) => {
                            onFieldValueChange?.(name, ids.join(','));
                        }}
                        validationSchema={{
                            fileSize: validation?.onChange?.fileSize || undefined,
                            fileCount: validation?.onChange?.fileCount || undefined,
                            fileType: validation?.onChange?.fileType || undefined,
                        }}
                    />
                    <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                </>
            );
        }
        case 'select': {
            let alteredValue = '';
            let alteredInputValue = resolvedValue || '';
            let renderOtherTextField = false;
            const optionsValueArr = selectOptions?.map((opt) => opt.selectItemValue?.toString());
            const isOtherOrAutre = (val: string) => ['Other', 'Autre'].includes(val);

            if (disabled) {
                alteredValue = '';
                if (isOtherTextInputEnable) {
                    renderOtherTextField = true;
                }
            } else {
                if (isOtherTextInputEnable) {
                    if (
                        optionsValueArr?.includes(resolvedValue) &&
                        !isOtherOrAutre(resolvedValue)
                    ) {
                        alteredValue = resolvedValue;
                        renderOtherTextField = false;
                    } else if (
                        isOtherOrAutre(resolvedValue) ||
                        (!optionsValueArr?.includes(resolvedValue) && resolvedValue !== '')
                    ) {
                        alteredValue = optionsValueArr?.includes('Other')
                            ? 'Other'
                            : optionsValueArr?.includes('Autre')
                              ? 'Autre'
                              : 'Other';
                        alteredInputValue = isOtherOrAutre(resolvedValue) ? '' : resolvedValue;
                        renderOtherTextField = true;
                    }
                } else {
                    alteredValue = resolvedValue;
                    renderOtherTextField = false;
                }
            }

            return (
                <FormControl
                    sx={{
                        '& .MuiFormHelperText-root': {
                            marginLeft: '0px',
                        },
                    }}
                    fullWidth
                >
                    <Select
                        inputProps={{ tabindex: tabindex }}
                        error={isError}
                        disabled={disabled}
                        name={name}
                        placeholder={placeholder}
                        sx={{
                            ...selectFieldStyles,
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        value={alteredValue || ''}
                        renderValue={(selected: any) => {
                            if (selected === null || selected === undefined || selected === '') {
                                return placeholder;
                            }
                            return selected;
                        }}
                        onChange={(event: SelectChangeEvent<any>) => {
                            if (!readOnly && onFieldValueChange) {
                                const selectedValue = event.target.value;

                                if (isOtherOrAutre(selectedValue)) {
                                    renderOtherTextField = true;
                                    alteredInputValue = '';
                                } else {
                                    renderOtherTextField = false;
                                }

                                onFieldValueChange(name, selectedValue);
                            }
                        }}
                    >
                        {placeholder && (
                            <MenuItem disabled key="placeholder" value="">
                                {placeholder}
                            </MenuItem>
                        )}
                        {selectOptions?.map((option) => (
                            <MenuItem
                                key={option.selectItemKey}
                                value={option.selectItemValue}
                                disabled={option.isSelectItemDisabled}
                            >
                                {option.selectItemLabel}
                            </MenuItem>
                        ))}
                    </Select>
                    {showSelectBoxValue && (
                        <Box>
                            {selectOptions?.map((option, index) => (
                                <Typography key={index} variant="body2" sx={selectFieldStyles}>
                                    {option.selectItemValue}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {renderOtherTextField && isOtherTextInputEnable && (
                        <Box sx={{ width: '100%' }}>
                            <Typography
                                variant={isMobile ? 'body2' : 'body1'}
                                sx={{
                                    marginTop: '8px',
                                    color: disabled
                                        ? theme.palette.neutral.gray
                                        : theme.palette.neutral.black,
                                }}
                            >
                                {formValidationTranslations.specifyLendingInstitution}
                            </Typography>
                            <TextField
                                sx={{ ...textFieldStyle, marginTop: '8px' }}
                                value={alteredInputValue}
                                disabled={disabled}
                                error={isError}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => {
                                    if (!readOnly && onFieldValueChange) {
                                        const inputValue = event.target.value;
                                        alteredInputValue = inputValue;

                                        const baseOtherValue = optionsValueArr?.includes('Other')
                                            ? 'Other'
                                            : optionsValueArr?.includes('Autre')
                                              ? 'Autre'
                                              : 'Other';

                                        onFieldValueChange(name, inputValue || baseOtherValue);
                                    }
                                }}
                            />
                        </Box>
                    )}

                    <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                </FormControl>
            );
        }

        case 'date': {
            const dateFieldValue = resolvedValue
                ? DateTime.fromJSDate(new Date(resolvedValue)).isValid
                    ? DateTime.fromJSDate(new Date(resolvedValue))
                    : undefined
                : undefined;
            return (
                <Box sx={{ width: '100%' }}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker
                            inputRef={DatePickerInputRef}
                            disabled={disabled}
                            sx={{
                                width: '100%',
                                marginTop: '6px',
                                '& .MuiOutlinedInput-notchedOutline': isError
                                    ? { border: '1px solid', borderColor: 'error.main' }
                                    : {},
                            }}
                            value={dateFieldValue ?? null}
                            onChange={(newValue) => {
                                if (onFieldValueChange) {
                                    onFieldValueChange(name, newValue);
                                }
                            }}
                        />
                    </LocalizationProvider>
                    <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                </Box>
            );
        }
        case 'table': {
            return (
                <Box sx={{ width: '100%', mt: 1 }}>
                    <TableFormComponent
                        name={name}
                        columns={columns || []}
                        value={resolvedValue}
                        disabled={disabled}
                        readOnly={readOnly}
                        isError={isError}
                        selectedLawFirmId={lawFirmId}
                        language={language}
                        rowCount={schema.rowCount}
                        onValueChange={onFieldValueChange}
                    />
                    <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                </Box>
            );
        }
        case 'contactInfoSection': {
            return (
                <Box sx={{ width: '100%' }}>
                    <ContactBlockQuestion
                        schema={schema}
                        isRequired={!!validation?.required}
                        value={typeof resolvedValue === 'string' ? resolvedValue : undefined}
                        entryErrors={entryErrors}
                        disabled={disabled}
                        readOnly={readOnly}
                        lawFirmId={lawFirmId}
                        language={language}
                        onValueChange={onFieldValueChange}
                    />
                    <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                </Box>
            );
        }
        case 'multiselect': {
            const selectedValues = parseMultiSelectValue(resolvedValue);

            const handleToggle = (optionValue: string) => {
                if (readOnly || !onFieldValueChange) return;
                const nextSelected = sortMultiSelectByOptions(
                    selectedValues.includes(optionValue)
                        ? selectedValues.filter((v) => v !== optionValue)
                        : [...selectedValues, optionValue],
                    options,
                );
                onFieldValueChange(name, stringifyMultiSelectValue(nextSelected));
            };

            return (
                <Box sx={{ width: '100%' }}>
                    <FormControl
                        sx={{
                            marginTop: '4px',
                            marginLeft: '8px',
                            pointerEvents: readOnly ? 'none' : undefined,
                        }}
                        fullWidth
                    >
                        {options?.map((option, index) => {
                            const optionValue = String(option.optionValue);
                            return (
                                <FormControlLabel
                                    key={index}
                                    tabIndex={tabIndex}
                                    control={
                                        <Checkbox
                                            checked={selectedValues.includes(optionValue)}
                                            disabled={disabled}
                                            onChange={() => handleToggle(optionValue)}
                                            sx={{
                                                color: isError
                                                    ? theme.palette.error.main
                                                    : 'neutral.lightGray',
                                                '&.Mui-checked': {
                                                    color: isError
                                                        ? theme.palette.error.main
                                                        : 'primary.main',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: '14px',
                                                color: isError
                                                    ? theme.palette.error.main
                                                    : disabled
                                                      ? theme.palette.neutral.gray
                                                      : theme.palette.neutral.black,
                                            }}
                                        >
                                            {option.optionLabel}
                                        </Typography>
                                    }
                                />
                            );
                        })}
                        <FormHelperText error={isError}>{isError ? helper : ''}</FormHelperText>
                    </FormControl>
                </Box>
            );
        }
        default:
            return null;
    }
}
